import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';


const RuleEditorModal = ({ isOpen, onClose, rule = null, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    jurisdiction: '',
    category: '',
    severity: 'medium',
    status: 'active',
    description: '',
    regulatoryText: '',
    aiParameters: {
      keywords: [],
      contextWeight: 0.7,
      strictMode: false,
      autoFlag: true
    },
    citations: [],
    effectiveDate: '',
    expiryDate: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule?.name || '',
        jurisdiction: rule?.jurisdiction || '',
        category: rule?.category || '',
        severity: rule?.severity || 'medium',
        status: rule?.status || 'active',
        description: rule?.description || '',
        regulatoryText: rule?.regulatoryText || '',
        aiParameters: {
          keywords: rule?.aiParameters?.keywords || [],
          contextWeight: rule?.aiParameters?.contextWeight || 0.7,
          strictMode: rule?.aiParameters?.strictMode || false,
          autoFlag: rule?.aiParameters?.autoFlag || true
        },
        citations: rule?.citations || [],
        effectiveDate: rule?.effectiveDate || '',
        expiryDate: rule?.expiryDate || ''
      });
    } else {
      // Reset form for new rule
      setFormData({
        name: '',
        jurisdiction: '',
        category: '',
        severity: 'medium',
        status: 'active',
        description: '',
        regulatoryText: '',
        aiParameters: {
          keywords: [],
          contextWeight: 0.7,
          strictMode: false,
          autoFlag: true
        },
        citations: [],
        effectiveDate: '',
        expiryDate: ''
      });
    }
    setErrors({});
  }, [rule, isOpen]);

  const jurisdictionOptions = [
    { value: 'federal', label: 'Federal' },
    { value: 'cfpb', label: 'CFPB' },
    { value: 'fdic', label: 'FDIC' },
    { value: 'occ', label: 'OCC' },
    { value: 'fed', label: 'Federal Reserve' },
    { value: 'sec', label: 'SEC' },
    { value: 'finra', label: 'FINRA' }
  ];

  const categoryOptions = [
    { value: 'disclosure', label: 'Disclosure Requirements' },
    { value: 'lending', label: 'Lending Practices' },
    { value: 'privacy', label: 'Privacy & Data Protection' },
    { value: 'advertising', label: 'Advertising Standards' },
    { value: 'aml', label: 'Anti-Money Laundering' },
    { value: 'kyc', label: 'Know Your Customer' },
    { value: 'fair-lending', label: 'Fair Lending' },
    { value: 'consumer-protection', label: 'Consumer Protection' }
  ];

  const severityOptions = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending Review' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAIParameterChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      aiParameters: {
        ...prev?.aiParameters,
        [field]: value
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Rule name is required';
    }
    
    if (!formData?.jurisdiction) {
      newErrors.jurisdiction = 'Jurisdiction is required';
    }
    
    if (!formData?.category) {
      newErrors.category = 'Category is required';
    }
    
    if (!formData?.regulatoryText?.trim()) {
      newErrors.regulatoryText = 'Regulatory text is required';
    }
    
    if (!formData?.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving rule:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-card border border-border rounded-lg shadow-modal overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {rule ? 'Edit Rule' : 'Add New Rule'}
            </h2>
            <p className="text-sm text-muted-foreground">
              Configure regulatory compliance rule for automated content analysis
            </p>
          </div>
          <Button variant="ghost" iconName="X" onClick={onClose} />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Rule Name"
                placeholder="Enter rule name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />
              
              <Select
                label="Jurisdiction"
                placeholder="Select jurisdiction"
                options={jurisdictionOptions}
                value={formData?.jurisdiction}
                onChange={(value) => handleInputChange('jurisdiction', value)}
                error={errors?.jurisdiction}
                required
              />
              
              <Select
                label="Category"
                placeholder="Select category"
                options={categoryOptions}
                value={formData?.category}
                onChange={(value) => handleInputChange('category', value)}
                error={errors?.category}
                required
              />
              
              <Select
                label="Severity Level"
                options={severityOptions}
                value={formData?.severity}
                onChange={(value) => handleInputChange('severity', value)}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                className="w-full h-24 px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Brief description of the rule..."
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
              />
            </div>

            {/* Regulatory Text */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Regulatory Text <span className="text-error">*</span>
              </label>
              <textarea
                className="w-full h-32 px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Enter the full regulatory text or requirement..."
                value={formData?.regulatoryText}
                onChange={(e) => handleInputChange('regulatoryText', e?.target?.value)}
              />
              {errors?.regulatoryText && (
                <p className="text-sm text-error mt-1">{errors?.regulatoryText}</p>
              )}
            </div>

            {/* AI Parameters */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h3 className="text-lg font-medium text-foreground mb-4">AI Analysis Parameters</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Context Weight
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData?.aiParameters?.contextWeight}
                    onChange={(e) => handleAIParameterChange('contextWeight', parseFloat(e?.target?.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Loose (0.0)</span>
                    <span className="font-medium">{formData?.aiParameters?.contextWeight}</span>
                    <span>Strict (1.0)</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Keywords (comma-separated)
                  </label>
                  <Input
                    placeholder="keyword1, keyword2, keyword3"
                    value={formData?.aiParameters?.keywords?.join(', ')}
                    onChange={(e) => handleAIParameterChange('keywords', e?.target?.value?.split(',')?.map(k => k?.trim())?.filter(k => k))}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Checkbox
                  label="Strict Mode"
                  description="Enable stricter pattern matching"
                  checked={formData?.aiParameters?.strictMode}
                  onChange={(e) => handleAIParameterChange('strictMode', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Auto-flag Violations"
                  description="Automatically flag content that violates this rule"
                  checked={formData?.aiParameters?.autoFlag}
                  onChange={(e) => handleAIParameterChange('autoFlag', e?.target?.checked)}
                />
              </div>
            </div>

            {/* Dates and Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                type="date"
                label="Effective Date"
                value={formData?.effectiveDate}
                onChange={(e) => handleInputChange('effectiveDate', e?.target?.value)}
                error={errors?.effectiveDate}
                required
              />
              
              <Input
                type="date"
                label="Expiry Date"
                value={formData?.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', e?.target?.value)}
              />
              
              <Select
                label="Status"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleInputChange('status', value)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleSave}
            loading={isLoading}
            iconName="Save"
            iconPosition="left"
          >
            {rule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RuleEditorModal;